import React, {
  useRef,
  useEffect,
  useState,
  useLayoutEffect,
  useCallback,
  useMemo,
  useContext,
} from "react";

import { UserPreferences } from "./preferences";
import {
  LocalStorageSchema,
  getConverter,
  getLatestVersion,
} from "./localStorage";
import { RouteStateContext } from "./routeState";
import { noop } from "./noop";
import isEqual from "lodash/isEqual";
import moment, { Moment } from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import { SortDirection } from "types/types";

export function useInvalidate() {
  const [, setN] = useState(0);
  const incr = useCallback(() => {
    setN((n) => n + 1);
  }, []);
  return incr;
}

export function useUnmounted(): { current: boolean } {
  const unmounted = useRef(false);
  useEffect(() => {
    return () => {
      unmounted.current = true;
    };
  }, []);
  return unmounted;
}

export function usePrevious<T>(value: T): T | undefined {
  const [previous, setPrevious] = useState<T | undefined>(undefined);
  useEffect(() => {
    setPrevious(value);
  }, [value]);
  return previous;
}

export function useDeepEqualMemo<T>(value: T): T {
  const ref = useRef(value);
  if (!isEqual(value, ref.current)) {
    ref.current = value;
  }
  return ref.current;
}

export type Dimensions = {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
};

export function useDimensions<T extends Element>(): [
  (elem: T) => void,
  Dimensions | undefined,
] {
  const resizeTimeoutMillis = 100;
  const [elem, setElem] = useState<T>();
  const [dimensions, setDimensions] = useState<Dimensions | undefined>(
    undefined,
  );

  useLayoutEffect(() => {
    let lastResize: number | undefined;
    const handleResize = () => {
      if (lastResize) {
        window.clearTimeout(lastResize);
      }
      lastResize = window.setTimeout(() => {
        if (!elem) {
          return;
        }
        const bounds = elem.getBoundingClientRect();
        setDimensions({
          top: bounds.top,
          right: bounds.right,
          bottom: bounds.bottom,
          left: bounds.left,
          width: bounds.width,
          height: bounds.height,
        });
        lastResize = undefined;
      }, resizeTimeoutMillis);
    };
    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (lastResize) {
        window.clearTimeout(lastResize);
      }
    };
  }, [elem]);
  return [setElem, dimensions];
}

export const useTimeout = (
  fn: (...args: any[]) => any,
  timeout: number,
  deps: React.DependencyList,
) => {
  // since any timeout callback will need to be wrapped in useCallback to be
  // useful, we do that for the caller, so we need to disable it as a callback dep
  const cb = useCallback(fn, deps); // eslint-disable-line react-hooks/exhaustive-deps, react-hooks/use-memo
  useEffect(() => {
    const timeoutId = setTimeout(cb, timeout);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [cb, timeout]);
};

export const useInterval = (
  fn: (...args: any[]) => any,
  interval: number,
  deps: React.DependencyList,
) => {
  // same reasoning as useTimeout: the callback will need to be wrapped in useCallback to
  // be useful, so we do that for the caller, so we need to disable it as a callback dep
  const cb = useCallback(fn, deps); // eslint-disable-line react-hooks/exhaustive-deps, react-hooks/use-memo
  useEffect(() => {
    const intervalId = setInterval(cb, interval);
    return () => {
      clearInterval(intervalId);
    };
  }, [cb, interval]);
};

// Returns the current time, re-rendering on an interval.
export function useNow(updateIntervalMs = 60_000) {
  const [now, setNow] = useState(() => Date.now());
  useInterval(() => setNow(Date.now()), updateIntervalMs, []);
  return now;
}

// Returns a function that, when invoked, only executes the provided function
// after a timeout elapses. If invoked again before the timeout elapses, the
// previous invocation is canceled and the timeout is reset. If a different
// function (by reference) is passed in, any previous pending invocation is
// canceled, so callers should use useCallback as appropriate.
export const useDebounced = <T extends unknown[]>(
  fn: (...args: T) => void,
  timeout: number,
): ((...args: T) => void) => {
  const lastTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();
  const wrapper = useCallback(
    (...args: T) => {
      if (lastTimeout.current) {
        clearTimeout(lastTimeout.current);
      }
      lastTimeout.current = setTimeout(fn, timeout, ...args);
    },
    [fn, timeout],
  );
  // N.B.: This runs when `wrapper` changes and is here to avoid previous timeouts
  // from previous wrappers firing when the wrapper is changed. However, this effect
  // runs after the host component first renders, so if the wrapper is invoked in
  // the course of that render (immediately after the call to the hook), or during
  // layout, it may end up cancelled before it's actually executed. This is not an
  // intended use case, so it's an acceptable limitation for now.
  useEffect(() => {
    if (lastTimeout.current) {
      clearTimeout(lastTimeout.current);
    }
  }, [wrapper]);

  return timeout > 0 ? wrapper : fn;
};

export const useCycle = (stepCount: number, interval: number): number => {
  const [curr, setCurr] = useState(0);
  useInterval(
    () => {
      setCurr((val) => (val + 1) % stepCount);
    },
    interval,
    [stepCount],
  );
  return curr;
};

type RouteUpdateOpts = { replace?: boolean };
type RouteState<T> = [
  T | undefined,
  (newValue: T | undefined, opts?: RouteUpdateOpts) => void,
];

export const useRouteSearchState = <T>(args: {
  key: string;
  // N.B.: *not* URL-encoded
  decode: (encoded: string) => T;
  // N.B.: also *not* URL-encoded
  encode: (value: T) => string;
}): RouteState<T> => {
  const { key, encode, decode } = args;
  const [rawRouteState, updateRawRouteState] = useContext(RouteStateContext);

  const strVal = rawRouteState.search[key];
  const value = useMemo(() => {
    if (strVal == null || strVal === "") {
      return undefined;
    }

    return decode(strVal);
  }, [decode, strVal]);
  const updater = useCallback(
    (newVal: T | undefined, opts?: RouteUpdateOpts): void => {
      const encoded = newVal && encode(newVal);
      if (encoded === strVal) {
        return;
      }
      updateRawRouteState({ search: { [key]: encoded }, ...opts });
    },
    [encode, key, strVal, updateRawRouteState],
  );

  return [value, updater];
};

export const useRouteHashState = <T>(args: {
  decode: (encoded: string) => T;
  encode: (value: T) => string;
}): RouteState<T> => {
  const { encode, decode } = args;
  const [rawRouteState, updateRawRouteState] = useContext(RouteStateContext);
  const strVal = rawRouteState.hash;
  const value = useMemo(() => {
    if (strVal == null || strVal === "") {
      return undefined;
    }

    return decode(strVal);
  }, [decode, strVal]);

  const updater = useCallback(
    (newVal: T | undefined, opts?: RouteUpdateOpts): void => {
      const encoded = newVal && encode(newVal);
      if (encoded === strVal) {
        return;
      }
      updateRawRouteState({ hash: encoded, ...opts });
    },
    [encode, strVal, updateRawRouteState],
  );

  return [value, updater];
};

// Like useState, but backed by LocalStorage. Since we know what we have
// stored, usage must conform to the LocalStorageSchema type. Each keyed
// subsection of localStorage is versioned, may evolve independently, and
// must decode previous values via an explicit Converter.
type LSSection<K extends keyof LocalStorageSchema> =
  | LocalStorageSchema[K]["current"]
  | undefined;

export const useLocalStorage = <K extends keyof LocalStorageSchema>(
  key: K,
): [LSSection<K>, React.Dispatch<React.SetStateAction<LSSection<K>>>] => {
  const prefixedKey = "_pga_" + key;
  const invalidate = useInvalidate();

  const encoded = window.localStorage.getItem(prefixedKey);
  const value = decodeValue(key, encoded);
  const cachedValue = useDeepEqualMemo(value);

  const updater = useCallback(
    (value: React.SetStateAction<LSSection<K>>) => {
      if (value === undefined) {
        window.localStorage.removeItem(prefixedKey);
        return;
      }

      try {
        let newValue: LSSection<K>;
        if (typeof value === "function") {
          const existingEncoded = window.localStorage.getItem(prefixedKey);
          const existing = decodeValue(key, existingEncoded);
          newValue = value(existing);
        } else {
          newValue = value;
        }

        const version = getLatestVersion(key);
        const encoded = JSON.stringify({ ...newValue, version });
        window.localStorage.setItem(prefixedKey, encoded);
      } catch (error) {
        console.error("could not save localStorage value", error);
      }

      invalidate();
    },
    [key, prefixedKey, invalidate],
  );

  return [cachedValue, updater];
};

function decodeValue<K extends keyof LocalStorageSchema>(
  key: K,
  encoded: string | null | undefined,
) {
  if (encoded == null) {
    return undefined;
  }
  try {
    const decoded = JSON.parse(encoded) as LocalStorageSchema[K]["known"];
    const converter = getConverter(key);
    const converted = converter(decoded);
    return converted;
  } catch (error) {
    console.error("could not load localStorage value", error);
    return undefined;
  }
}

// Retrieve a specific preferences section and its updater. Note that the updater
// is expected to provide the full section value: the new value is not merged with
// the existing one.
export const useUserPreferences = <K extends keyof UserPreferences>(
  section: K,
): [
  Partial<UserPreferences[K]>,
  (newValue: Partial<UserPreferences[K]>) => void,
] => {
  const [prefs, setPrefs] = useLocalStorage("preferences");
  const prefsRef = useRef<typeof prefs>();
  prefsRef.current = prefs;
  const updatePrefs = useCallback(
    (newValue: Partial<UserPreferences[K]>): void => {
      // TODO: not sure why this cast is necessary: with strictNullChecks enabled,
      // tsc seems to infer the type of `merged` as `{}`.
      const merged = {
        ...prefsRef.current,
        [section]: newValue,
      } as UserPreferences;
      setPrefs(merged);
    },
    [section, prefsRef, setPrefs],
  );
  const prefsSection = prefs?.[section] ?? {};
  return [prefsSection, updatePrefs];
};

export const useRecentTimeRange = (
  amount: moment.DurationInputArg1,
  unit: moment.unitOfTime.DurationConstructor,
  cacheFor?: moment.Duration,
) => {
  const cache = useRef<{ expiresAt: Moment; value: [Moment, Moment] }>();
  const now = moment();
  if (!cache.current || cache.current.expiresAt.isSameOrBefore(now)) {
    const expiresAt = now
      .clone()
      .add(cacheFor ?? moment.duration(5, "minutes"));
    cache.current = {
      expiresAt,
      value: [now.clone().subtract(amount, unit), now],
    };
  }
  return cache.current.value;
};

/**
 * Return the passed-in Moment, or the previously passed-in Moment if that
 * previous timestamp is within the specified tolerance. Returns undefined
 * if passed undefined.
 *
 * This is useful to avoid expensive operations (e.g., GraphQL queries) when
 * parameters change only slightly.
 *
 * @param currentValue
 * @param tolerance
 * @returns a Moment instance, possible cached
 */
export const useCachedTimestamp = (
  currentValue: Moment | undefined,
  tolerance?: moment.Duration,
) => {
  const cache = useRef<Moment>();
  if (!currentValue) {
    return undefined;
  }
  const toleranceMs = (
    tolerance ?? moment.duration(5, "minutes")
  ).asMilliseconds();
  if (
    !cache.current ||
    Math.abs(cache.current.diff(currentValue)) > toleranceMs
  ) {
    cache.current = currentValue;
  }
  return cache.current;
};

export function usePauseAfter(ms: number): [boolean, React.Dispatch<boolean>] {
  const [paused, setPaused] = useState(false);
  const [lastUnpaused, setLastUnpaused] = useState(() => Date.now());
  useEffect(() => {
    setLastUnpaused(Date.now());
  }, [paused, setLastUnpaused]);
  useTimeout(
    () => {
      setPaused(true);
    },
    ms,
    [
      setPaused,
      lastUnpaused /* NB: only here to re-trigger the timeout when necessary */,
    ],
  );

  return [paused, setPaused];
}

export function useDismissable(
  id: string | undefined,
): [isDismissed: boolean, setDismissed: () => void] {
  const [flashPrefs, setFlashPrefs] = useUserPreferences("flash");
  if (!id) {
    return [false, noop];
  }
  const dismissed = flashPrefs.dismissed ?? [];
  function handleDismiss() {
    if (!id) {
      // This should not happen, but it keeps TypeScript happy
      return;
    }
    // to avoid growing this array indefinitely, we assume that older dismissed
    // banners are irrelevant past a certain threshold
    const cutoff = moment().subtract(6, "months").toDate().getTime();
    const newDismissed = dismissed
      .filter((d) => d.ts > cutoff)
      .concat({
        id,
        ts: Date.now(),
      });
    setFlashPrefs({
      dismissed: newDismissed,
    });
  }
  const isDismissed = dismissed.some((item) => item.id === id);
  return [isDismissed, handleDismiss];
}

type SortProps<T extends string> = {
  sortBy: T;
  sortDirection: SortDirection;
};

export function useQueryStringSort<SortBy extends string>({
  param,
  sortBy,
  sortDirection,
}: {
  param: string | false;
} & SortProps<SortBy>) {
  const loc = useLocation();
  const navigate = useNavigate();
  const rawValue =
    typeof param == "string"
      ? new URLSearchParams(loc.search).get(param)
      : undefined;

  const parsed = parseSortOrder<SortBy>(rawValue) ?? {
    sortBy,
    sortDirection,
    fromURL: false,
  };

  const updateURLSort = useCallback(
    (props?: SortProps<SortBy>) => {
      if (typeof param != "string") {
        return;
      }
      const formatted = formatSortOrder(props);

      const newSearch = new URLSearchParams(window.location.search);
      if (formatted) {
        newSearch.set(param, formatted);
      } else {
        newSearch.delete(param);
      }
      const hash = window.location.hash;
      navigate({ search: newSearch.toString(), hash }, { replace: true });
    },
    [param, navigate],
  );

  return [parsed, updateURLSort] as const;
}

function parseSortOrder<SortBy extends string>(
  rawValue?: string | null,
): (SortProps<SortBy> & { fromURL: true }) | undefined {
  if (!rawValue) {
    return undefined;
  }
  const [sortBy, sortDirection] = rawValue.split(".");
  if (sortDirection == "asc") {
    return { sortBy: sortBy as SortBy, sortDirection: "ASC", fromURL: true };
  } else if (sortDirection == "desc") {
    return { sortBy: sortBy as SortBy, sortDirection: "DESC", fromURL: true };
  } else {
    return undefined;
  }
}

function formatSortOrder<SortBy extends string>(props?: SortProps<SortBy>) {
  if (!props) {
    return undefined;
  }
  const { sortBy, sortDirection } = props;
  return [sortBy, sortDirection.toLowerCase()].join(".");
}

// Returns a callback that navigates back when there's a prior in-app history
// entry, otherwise navigates to the given fallback path. Useful for Cancel/Back
// buttons that shouldn't leave the app when the page was loaded directly.
export function useGoBackOr(fallbackPath: string): () => void {
  const navigate = useNavigate();
  return () => {
    // react-router records an incrementing `idx` on history.state; idx > 0 means
    // there's an earlier in-app entry to return to.
    const idx = (window.history.state?.idx as number | undefined) ?? 0;
    if (idx > 0) {
      navigate(-1);
    } else {
      navigate(fallbackPath);
    }
  };
}
